import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import GoldButton from '../components/GoldButton';
import StatusBadge from '../components/StatusBadge';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';

// Import refactored components
import ClientInfoSection from '../components/admin/ClientInfoSection';
import DeliverySection from '../components/admin/DeliverySection';
import DocumentsSection from '../components/admin/DocumentsSection';
import CommunicationModal from '../components/admin/CommunicationModal';
import ConcesionarioModal from '../components/admin/ConcesionarioModal';
import VerificationCountdown from '../components/admin/VerificationCountdown';

export default function AdminDetail() {
  const { cedula } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [beneficiario, setBeneficiario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showConcesionarioModal, setShowConcesionarioModal] = useState(false);
  
  // Form data
  const [modeloMoto, setModeloMoto] = useState('');
  const [precioTotal, setPrecioTotal] = useState('');
  const [valorCuota, setValorCuota] = useState('');
  const [fechaEntrega, setFechaEntrega] = useState('');
  const [documentos, setDocumentos] = useState([]);
  const [pagosSeguros, setPagosSeguros] = useState(0);
  const [concesionarioSeleccionado, setConcesionarioSeleccionado] = useState(null);

  useEffect(() => {
    api.get('/auth/me')
      .then(() => fetchBeneficiario())
      .catch(() => navigate('/admin/login', { replace: true }));
  }, [cedula, navigate]);

  const fetchBeneficiario = async () => {
    try {
      const response = await api.get(`/admin/beneficiarios/${cedula}`);
      setBeneficiario(response.data);
      
      const df = response.data.datos_financieros || {};
      setModeloMoto(df.modelo_moto || '');
      setPrecioTotal(df.precio_total || '');
      setValorCuota(df.valor_cuota || '');
      setFechaEntrega(response.data.fecha_entrega || '');
      setDocumentos(response.data.documentos || []);
      
      fetchPagosSeguros(cedula);
    } catch (error) {
      toast.error('Error al cargar beneficiario');
      navigate('/admin/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPagosSeguros = async (cedulaCliente) => {
    try {
      setPagosSeguros(0);
    } catch (error) {
      setPagosSeguros(0);
    }
  };

  const calcularFinanciamiento = () => {
    const precio = parseFloat(precioTotal) || 0;
    const aporte = beneficiario?.datos_financieros?.total_aportado ?? beneficiario?.datos_financieros?.aporte ?? 0;
    const cuota = parseFloat(valorCuota) || 0;
    
    const restante = precio - aporte;
    const tiempoRestante = cuota > 0 ? restante / cuota : 0;
    
    return {
      aporte,
      restante: restante > 0 ? restante : 0,
      tiempoRestante: tiempoRestante > 0 ? tiempoRestante : 0
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const calc = calcularFinanciamiento();
      await api.put(`/admin/beneficiarios/${cedula}`, {
        datos_financieros: {
          modelo_moto: modeloMoto,
          precio_total: parseFloat(precioTotal) || 0,
          valor_cuota: parseFloat(valorCuota) || 0,
          aporte: calc.aporte,
          total_aportado: calc.aporte,
          restante: calc.restante,
          tiempo_restante: calc.tiempoRestante
        },
        documentos: documentos,
        fecha_entrega: fechaEntrega || null
      });
      
      toast.success('Cambios guardados');
      fetchBeneficiario();
    } catch (error) {
      toast.error('Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncStrapi = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/admin/beneficiarios/${cedula}/sync-strapi`);
      const total = res.data?.total_aportado || 0;
      toast.success(
        `Sincronizado con Strapi. Total aportado: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total)}`
      );
      fetchBeneficiario();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sincronizar con Strapi');
    } finally {
      setSyncing(false);
    }
  };

  const handleAprobar = async () => {    setSaving(true);
    try {
      await api.put(`/admin/beneficiarios/${cedula}`, { estado: 'aprobado' });
      toast.success('¡Beneficio aprobado!');
      fetchBeneficiario();
    } catch (error) {
      toast.error('Error al aprobar beneficio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/beneficiarios/${cedula}`);
      toast.success('Beneficiario eliminado');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Error al eliminar beneficiario');
    }
  };

  const handleReasignar = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/beneficiarios/${cedula}`, {
        estado: 'en_proceso',
        wizard_completado: false,
        colores_seleccionados: []
      });
      toast.success('Cliente re-asignado al inicio del proceso');
      fetchBeneficiario();
    } catch (error) {
      toast.error('Error al re-asignar');
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentoValidation = (index, isValid) => {
    const newDocs = [...documentos];
    newDocs[index].estado = isValid ? 'validado' : 'rechazado';
    setDocumentos(newDocs);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="text-[#F5A623] font-heading text-xl">Cargando...</div>
      </div>
    );
  }

  const calc = calcularFinanciamiento();

  return (
    <div className="min-h-screen bg-[#1A1A1A]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#1A1A1A]/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2 text-[#F5A623] hover:text-[#FF9500] transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-body">Volver al Dashboard</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-[white] font-bold mb-2">
              {beneficiario?.nombre || 'Beneficiario'}
            </h1>
            <p className="text-[#666666] font-body">Cédula: {beneficiario?.cedula}</p>
          </div>
          <StatusBadge estado={beneficiario?.estado} />
        </div>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6">
          <ClientInfoSection
            modeloMoto={modeloMoto}
            setModeloMoto={setModeloMoto}
            precioTotal={precioTotal}
            setPrecioTotal={setPrecioTotal}
            valorCuota={valorCuota}
            setValorCuota={setValorCuota}
            calc={calc}
            pagosSeguros={pagosSeguros}
          />

          <DeliverySection
            fechaEntrega={fechaEntrega}
            setShowCalendar={setShowCalendar}
            coloresSeleccionados={beneficiario?.colores_seleccionados}
            concesionarioAsignado={beneficiario?.concesionario_asignado}
            setShowConcesionarioModal={setShowConcesionarioModal}
            setShowEmailModal={setShowEmailModal}
          />
        </div>

        {/* Countdown de verificación (solo visible cuando está en verificación) */}
        <VerificationCountdown
          fechaVerificacionInicio={beneficiario?.fecha_verificacion_inicio}
          estado={beneficiario?.estado}
        />

        {/* Documents Section */}
        <DocumentsSection
          documentos={documentos}
          onDocumentoValidation={handleDocumentoValidation}
        />

        {/* Action Buttons */}
        <div className="mt-6 flex gap-4">
          <GoldButton
            onClick={handleSave}
            variant="secondary"
            disabled={saving}
            testId="save-changes-button"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </GoldButton>

          <GoldButton
            onClick={handleSyncStrapi}
            variant="secondary"
            disabled={syncing}
            testId="sync-strapi-button"
            className="bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30 hover:bg-[#3B82F6]/30"
          >
            {syncing ? 'Sincronizando...' : 'Sincronizar con Strapi'}
          </GoldButton>

          {beneficiario?.estado === 'en_proceso' && beneficiario?.wizard_completado && (
            <GoldButton
              onClick={handleAprobar}
              variant="primary"
              disabled={saving}
              testId="approve-benefit-button"
            >
              ¡APROBAR BENEFICIO!
            </GoldButton>
          )}

          {beneficiario?.wizard_completado && (
            <GoldButton
              onClick={handleReasignar}
              variant="secondary"
              disabled={saving}
              testId="reassign-button"
              className="bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30 hover:bg-[#F59E0B]/30"
            >
              RE-ASIGNAR
            </GoldButton>
          )}

          <button
            onClick={() => setShowDeleteModal(true)}
            className="ml-auto flex items-center gap-2 px-6 py-3 bg-[#EF4444]/20 text-[#EF4444] border-2 border-[#EF4444]/30 rounded-full hover:bg-[#EF4444]/30 transition-colors font-heading font-semibold"
            data-testid="delete-beneficiary-button"
          >
            <Trash2 className="w-5 h-5" />
            Eliminar Beneficiario
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-gradient-to-br from-[#2A2A2A] to-[#2A2A2A] border border-[#EF4444]/30 rounded-2xl p-8 max-w-md" data-testid="delete-confirmation-modal">
          <DialogTitle className="text-2xl font-heading font-bold text-[white] mb-4">
            ¿Eliminar Beneficiario?
          </DialogTitle>
          <DialogDescription className="text-[#666666] font-body mb-6">
            Esta acción no se puede deshacer. El beneficiario será eliminado permanentemente.
          </DialogDescription>
          <div className="flex gap-3">
            <GoldButton
              variant="ghost"
              onClick={() => setShowDeleteModal(false)}
              className="flex-1"
              testId="cancel-delete-button"
            >
              Cancelar
            </GoldButton>
            <button
              onClick={handleDelete}
              className="flex-1 px-6 py-3 bg-[#EF4444] text-white rounded-full hover:bg-[#DC2626] transition-colors font-heading font-bold"
              data-testid="confirm-delete-button"
            >
              Eliminar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendar Modal */}
      <Dialog open={showCalendar} onOpenChange={setShowCalendar}>
        <DialogContent className="bg-gradient-to-br from-[#2A2A2A] to-[#2A2A2A] border border-[#F5A623]/30 rounded-2xl p-8" data-testid="calendar-modal">
          <DialogTitle className="text-2xl font-heading font-bold text-[white] mb-4">
            Seleccionar Fecha de Entrega
          </DialogTitle>
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={fechaEntrega ? new Date(fechaEntrega) : undefined}
              onSelect={(date) => {
                if (date) {
                  setFechaEntrega(date.toISOString().split('T')[0]);
                  setShowCalendar(false);
                  toast.success('Fecha seleccionada');
                }
              }}
              className="rounded-md border border-white/10"
              disabled={(date) => date < new Date()}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Communication Modal */}
      <CommunicationModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        beneficiario={beneficiario}
      />

      {/* Concesionario Modal */}
      <ConcesionarioModal
        isOpen={showConcesionarioModal}
        onClose={() => setShowConcesionarioModal(false)}
        concesionarioSeleccionado={concesionarioSeleccionado}
        setConcesionarioSeleccionado={setConcesionarioSeleccionado}
      />
    </div>
  );
}
